import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function Rrsp() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="registered-retirement-savings-plan-rrsp" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Registered Retirement Savings Plan (RRSP)</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Save for Retirement with Tax Deductions and Tax-Deferred Growth
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is an RRSP? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is an RRSP?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                A Registered Retirement Savings Plan (RRSP) is a government-approved plan designed specifically to help Canadians save for retirement. Its key features are tax-deductible contributions and tax-deferred growth.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                When you contribute to an RRSP, you can deduct that amount from your taxable income for the year, potentially resulting in a tax refund. Inside the RRSP, your investments grow sheltered from tax until you withdraw the funds, typically in retirement when you may be in a lower tax bracket.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Contribution Limit:</strong> Your RRSP contribution limit is based on a percentage (18%) of your previous year's earned income, up to a maximum annual limit set by the government, plus any unused room carried forward from previous years. You can find your limit on your CRA Notice of Assessment.
                </li>
                <li>
                  <strong>Making Contributions:</strong> You contribute funds (up to your limit) to an RRSP account. Contributions made within the first 60 days of a calendar year can often be claimed for the previous tax year.
                </li>
                <li>
                  <strong>Tax Deduction:</strong> You claim your RRSP contributions as a deduction on your income tax return, reducing your taxable income for that year.
                </li>
                <li>
                  <strong>Investing Funds:</strong> Like a TFSA, an RRSP can hold various qualified investments (cash, GICs, bonds, stocks, mutual funds, ETFs).
                </li>
                <li>
                  <strong>Tax-Deferred Growth:</strong> Investment earnings (interest, dividends, capital gains) accumulate tax-free within the RRSP.
                </li>
                 <li>
                  <strong>Withdrawals:</strong> Funds withdrawn from an RRSP are considered taxable income in the year of withdrawal and are subject to withholding tax at the source (except under specific programs like the Home Buyers' Plan or Lifelong Learning Plan).
                 </li>
                 <li>
                  <strong>Maturity:</strong> You must wind up your RRSP by the end of the year you turn 71, typically by converting it to a Registered Retirement Income Fund (RRIF) or purchasing an annuity.
                 </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Tax Deduction:</strong> Reduces your current taxable income, potentially leading to a tax refund.</li>
                <li><strong>Tax-Deferred Growth:</strong> Investments grow faster without being taxed annually.</li>
                <li><strong>Retirement Savings:</strong> A primary vehicle for building long-term retirement funds.</li>
                <li><strong>Income Splitting Potential:</strong> Spousal RRSPs allow higher-income spouses to contribute for their lower-income spouse, potentially lowering the overall household tax burden in retirement.</li>
                <li><strong>Home Buyers' Plan (HBP):</strong> Allows first-time homebuyers to withdraw funds tax-free to buy or build a home (must be repaid over time).</li>
                <li><strong>Lifelong Learning Plan (LLP):</strong> Allows withdrawals to finance education or training (must be repaid).</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to keep in mind:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Withdrawals are Taxable:</strong> Unlike TFSAs, RRSP withdrawals are taxed as income.</li>
                <li><strong>Withholding Tax:</strong> Tax is withheld at the source on withdrawals (except HBP/LLP).</li>
                <li><strong>Contribution Limits:</strong> Over-contributions face penalties.</li>
                <li><strong>Impact on Income-Tested Benefits:</strong> RRSP/RRIF withdrawals count as income and can potentially reduce eligibility for benefits like OAS or GIS in retirement.</li>
                <li><strong>Mandatory Conversion at 71:</strong> Must convert to RRIF or annuity, leading to mandatory taxable withdrawals.</li>
                <li><strong>Less Ideal for Low-Income Earners:</strong> The tax deduction is less valuable if you are already in a low tax bracket. A TFSA might be preferable.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is an RRSP Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">RRSPs are a fundamental retirement savings tool, especially beneficial for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Individuals in their peak earning years who benefit significantly from the tax deduction.</li>
                <li>Those saving specifically for long-term retirement goals.</li>
                <li>People expecting to be in a lower tax bracket in retirement than they are currently.</li>
                <li>Couples looking to utilize spousal RRSPs for income splitting.</li>
                <li>Potential first-time homebuyers planning to use the Home Buyers' Plan.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Build a Secure Retirement with RRSPs</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Leverage the power of tax deductions and tax-deferred growth to reach your retirement goals. Let's review your RRSP strategy and contribution limits.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Your RRSP
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
